import React, { useState, useEffect } from "react";
import {
  Layout,
  Card,
  Typography,
  Button,
  Select,
  Tag,
  Spin,
  Upload,
} from "antd";
import { MenuOutlined, UploadOutlined } from "@ant-design/icons";
import axios from "axios";
import Sidebar from "../Components/Sidebar";
import { BASE_URL } from "../Config";

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const CATEGORIES = [
  { value: "yoga-content", label: "Inaccuracy in yoga content" },
  { value: "yoga-name",    label: "Error in yoga name" },
  { value: "yoga-image",   label: "Mistake in yoga image" },
  { value: "dictionary",   label: "Error in Yoga Dictionary" },
  { value: "other",        label: "Other issue" },
];

const ErrorReport = () => {
  const [collapsed, setCollapsed]     = useState(false);
  const [isMobile, setIsMobile]       = useState(false);
  const [mobileOpen, setMobileOpen]   = useState(false);
 const [form, setForm]     = useState({ category: "", description: "" });
  const [proofFile, setProofFile] = useState(null);
  const [stats, setStats]             = useState(null);
  const [reports, setReports]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [message, setMessage]         = useState(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    fetchStats();
    fetchMyReports();
  }, []);

  const getToken = () => sessionStorage.getItem("token");

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/error-report/monthly-stats`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res.data.success) setStats(res.data.data);
    } catch {}
  };

  const fetchMyReports = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/error-report/my-reports`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res.data.success) setReports(res.data.data);
    } catch {}
  };

  const handleSubmit = async () => {
    if (!form.category || !form.description) {
      setMessage({ type: "error", text: "Please fill in all fields." });
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      const formData = new FormData();
      formData.append("category", form.category);
      formData.append("description", form.description);
      if (proofFile) formData.append("proofImage", proofFile);

      const res = await axios.post(`${BASE_URL}/error-report/submit`, formData, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "multipart/form-data",
        },
      });
      if (res.data.success) {
        setMessage({ type: "success", text: res.data.data.message });
       setForm({ category: "", description: "" });
        setProofFile(null);
        fetchStats();
        fetchMyReports();
      }
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.error || "Submission failed.",
      });
    } finally {
      setLoading(false);
    }
  };

  const statusColor = (s) =>
    s === "valid" ? "green" : s === "invalid" ? "red" : "orange";

  const statusLabel = (s) =>
    s === "valid" ? "✓ Valid (+2 days)" :
    s === "invalid" ? "✗ Invalid" : "⏳ Pending";

  return (
    <Layout style={{ minHeight: "100vh", background: "#f5f7fa" }}>
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
        isMobile={isMobile}
        collapsedWidth="0"
        role="user"
      />

      <Layout
        style={{
          marginLeft: isMobile ? 0 : collapsed ? 0 : 240,
          transition: "margin 0.3s",
        }}
      >
        {/* Header */}
        <div className="MainHeader" style={{
          display: "flex", alignItems: "center",
          padding: isMobile ? "0 16px" : "0 24px",
        }}>
          {isMobile && (
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setMobileOpen(true)}
              style={{ fontSize: 18, marginRight: 12 }}
            />
          )}
          <Title level={4} style={{ margin: 0, flex: 1 }}>Report an Error</Title>
        </div>

        <Content style={{ padding: isMobile ? 12 : 24 }}>

          {/* Stats bar */}
          {stats && (
            <Card style={{ borderRadius: 12, marginBottom: 20, border: "1px solid #e5e7eb" }}
              bodyStyle={{ padding: "16px 24px" }}>
              <div style={{ display: "flex", gap: 40, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 26, fontWeight: 700 }}>
                    {stats.validThisMonth}
                    <span style={{ fontSize: 14, color: "#9ca3af", fontWeight: 400 }}>
                      /{stats.cap}
                    </span>
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>Valid this month</Text>
                </div>
                <div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: "#7c3aed" }}>
                    +{stats.daysEarned}
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>Days earned</Text>
                </div>
                <div>
                  <div style={{ fontSize: 26, fontWeight: 700 }}>{stats.remaining}</div>
                  <Text type="secondary" style={{ fontSize: 12 }}>Reports remaining</Text>
                </div>
              </div>
            </Card>
          )}

          {/* Submission form */}
          <Card style={{ borderRadius: 12, marginBottom: 24 }}
            bodyStyle={{ padding: 24 }}>
            <Title level={5} style={{ marginBottom: 16 }}>Submit a New Report</Title>
            <Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 20 }}>
              Found a mistake on yogarefer.com? Report it and earn a 2-day extension
              per valid report (max 10/month).
            </Text>

            {/* Category */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Category</div>
              <Select
                value={form.category || undefined}
                onChange={(val) => setForm({ ...form, category: val })}
                placeholder="Select a category"
                style={{ width: "100%" }}
                size="large"
              >
                {CATEGORIES.map((c) => (
                  <Option key={c.value} value={c.value}>{c.label}</Option>
                ))}
              </Select>
            </div>

            {/* Description */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Description</div>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={4}
                placeholder="Describe the error clearly — include the page URL, pose name, or dictionary entry if relevant."
                style={{
                  width: "100%", padding: "8px 12px", borderRadius: 8,
                  border: "1px solid #d1d5db", fontSize: 14,
                  resize: "vertical", boxSizing: "border-box",
                  fontFamily: "inherit",
                }}
              />
            </div>
{/* Proof image upload */}
<div style={{ marginBottom: 16 }}>
  <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
    Proof Image <span style={{ color: "#9ca3af", fontWeight: 400 }}>(optional)</span>
  </div>
  <Upload
    beforeUpload={(file) => { setProofFile(file); return false; }}
    onRemove={() => setProofFile(null)}
    fileList={proofFile ? [{ uid: "-1", name: proofFile.name, status: "done" }] : []}
    accept="image/*"
    maxCount={1}
    listType="picture"
  >
    <Button icon={<UploadOutlined />}>Upload Screenshot</Button>
  </Upload>
  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
    Screenshot showing the error on the page helps speed up review.
  </div>
</div>
            {/* Message */}
            {message && (
              <div style={{
                padding: "10px 14px", borderRadius: 8, marginBottom: 14, fontSize: 14,
                background: message.type === "success" ? "#f0fdf4" : "#fef2f2",
                color:      message.type === "success" ? "#16a34a" : "#dc2626",
                border: `1px solid ${message.type === "success" ? "#bbf7d0" : "#fecaca"}`,
              }}>
                {message.text}
              </div>
            )}

            <Button
              type="primary"
              size="large"
              loading={loading}
              onClick={handleSubmit}
              style={{ background: "#7c3aed", borderColor: "#7c3aed", borderRadius: 8 }}
            >
              Submit Report
            </Button>
          </Card>

          {/* Past reports */}
          {reports.length > 0 && (
            <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 24 }}>
              <Title level={5} style={{ marginBottom: 16 }}>My Reports</Title>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {reports.map((r) => (
                  <div key={r._id} style={{
                    border: "1px solid #e5e7eb", borderRadius: 10, padding: "12px 16px",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center", marginBottom: 6 }}>
                      <Text strong style={{ fontSize: 13 }}>
                        {CATEGORIES.find((c) => c.value === r.category)?.label || r.category}
                      </Text>
                      <Tag color={statusColor(r.status)}>{statusLabel(r.status)}</Tag>
                    </div>
                    <Text type="secondary" style={{ fontSize: 13 }}>{r.description}</Text>
                    {r.adminNote && (
                      <div style={{ marginTop: 6, fontSize: 12, color: "#9ca3af" }}>
                        Admin note: {r.adminNote}
                      </div>
                    )}
                    <div style={{ marginTop: 4, fontSize: 11, color: "#d1d5db" }}>
                      {new Date(r.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

        </Content>
      </Layout>
    </Layout>
  );
};

export default ErrorReport;